import { AcademyReceiptsBoundary } from '../components/StudentPaymentReceipts';
import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

export default function InvoiceOverview() {
  const companyId = localStorage.getItem('selectedCompanyId');
  return <AcademyReceiptsBoundary companyId={companyId}><GenericInvoiceOverview /></AcademyReceiptsBoundary>;
}

function GenericInvoiceOverview() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [dateFilter, setDateFilter] = useState('this_month');
  const [currencyFilter, setCurrencyFilter] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const getDateRange = (filter) => {
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
    switch (filter) {
      case 'today': return { from: new Date(y, m, d), to: new Date(y, m, d) };
      case 'this_week': { const day = now.getDay(); const diff = now.getDate() - day + (day === 0 ? -6 : 1); return { from: new Date(y, m, diff), to: now }; }
      case 'this_month': return { from: new Date(y, m, 1), to: now };
      case 'prev_month': return { from: new Date(y, m - 1, 1), to: new Date(y, m, 0) };
      case 'this_year': return { from: new Date(y, 0, 1), to: now };
      case 'custom': return { from: customFrom ? new Date(customFrom) : null, to: customTo ? new Date(customTo) : null };
      default: return { from: null, to: null };
    }
  };

  const fetchOverview = () => {
    if (!companyId) return;
    setLoading(true);
    const range = getDateRange(dateFilter);
    const params = new URLSearchParams();
    if (range.from) params.append('date_from', range.from.toISOString().split('T')[0]);
    if (range.to) params.append('date_to', range.to.toISOString().split('T')[0]);
    if (currencyFilter) params.append('currency', currencyFilter);

    fetch(`${API}/companies/${companyId}/invoicing/overview?${params}`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchOverview(); }, [companyId, dateFilter, currencyFilter, customFrom, customTo]);

  if (!companyId) return <div style={pageStyle}><p>Please select a company first.</p></div>;
  if (initialLoading) return <div style={pageStyle}><p>Loading...</p></div>;
  if (!data) return <div style={pageStyle}><p>Failed to load invoice overview.</p></div>;

  const s = data.summary || {};
  const fmt = (v) => parseFloat(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const summaryCards = [
    { label: 'Total Invoiced', value: '$' + fmt(s.total_invoiced), color: '#3b82f6' },
    { label: 'Total Collected', value: '$' + fmt(s.total_collected), color: '#10b981' },
    { label: 'Total Outstanding', value: '$' + fmt(s.total_outstanding), color: '#f59e0b' },
    { label: 'Unsettled Invoices', value: s.unsettled_invoices, color: '#ef4444' },
    { label: 'Overdue Invoices', value: s.overdue_invoices, color: '#dc2626' },
    { label: 'Invoices Issued', value: s.invoices_issued, color: '#8b5cf6' },
    { label: 'Receipts Issued', value: s.receipts_issued, color: '#06b6d4' },
  ];

  const statusSummary = data.status_summary || {};
  const paymentSummary = data.payment_summary || {};
  const unsettled = Array.isArray(data.unsettled_invoices) ? data.unsettled_invoices : [];
  const overdue = Array.isArray(data.overdue_invoices) ? data.overdue_invoices : [];
  const recentInv = Array.isArray(data.recent_invoices) ? data.recent_invoices : [];
  const recentRec = Array.isArray(data.recent_receipts) ? data.recent_receipts : [];
  const pending = Array.isArray(data.pending_actions) ? data.pending_actions : [];

  return (
    <div style={pageStyle}>
      <h2 style={{ margin: '0 0 20px', fontSize: '1.5rem', fontWeight: 700 }}>🧾 Invoice Overview</h2>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={dateFilter} onChange={e => setDateFilter(e.target.value)} style={selectStyle}>
          <option value="today">Today</option>
          <option value="this_week">This Week</option>
          <option value="this_month">This Month</option>
          <option value="prev_month">Previous Month</option>
          <option value="this_year">This Year</option>
          <option value="custom">Custom Range</option>
          <option value="">All Time</option>
        </select>
        {dateFilter === 'custom' && (
          <>
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={inputStyle} />
            <span style={{ color: '#64748b', fontSize: '0.85rem' }}>to</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} style={inputStyle} />
          </>
        )}
        <select value={currencyFilter} onChange={e => setCurrencyFilter(e.target.value)} style={selectStyle}>
          <option value="">All Currencies</option>
          <option value="USD">USD</option>
          <option value="LBP">LBP</option>
          <option value="EUR">EUR</option>
        </select>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        {summaryCards.map(c => (
          <div key={c.label} style={cardStyle}>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '4px' }}>{c.label}</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Status + Payment Summary side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
        <div style={{ ...cardStyle, padding: '16px' }}>
          <h3 style={sectionTitle}>📄 Document Status</h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {[
              { label: 'Draft', value: statusSummary.draft, color: '#94a3b8' },
              { label: 'Issued', value: statusSummary.issued, color: '#3b82f6' },
              { label: 'Paid', value: statusSummary.paid, color: '#10b981' },
              { label: 'Cancelled', value: statusSummary.cancelled, color: '#ef4444' },
            ].map(s => (
              <div key={s.label} style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: s.color }}>{s.value || 0}</div>
                <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ ...cardStyle, padding: '16px' }}>
          <h3 style={sectionTitle}>💰 Payment Status</h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {[
              { label: 'Unpaid', value: paymentSummary.unpaid, color: '#ef4444' },
              { label: 'Partially Paid', value: paymentSummary.partially_paid, color: '#f59e0b' },
              { label: 'Paid', value: paymentSummary.paid, color: '#10b981' },
            ].map(s => (
              <div key={s.label} style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: s.color }}>{s.value || 0}</div>
                <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pending Actions */}
      {pending.length > 0 && (
        <div style={{ ...cardStyle, marginBottom: '20px', padding: '16px' }}>
          <h3 style={sectionTitle}>⚠️ Pending Actions</h3>
          {pending.map((a, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: '0.85rem', color: '#334155' }}>{a.label}</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#ef4444' }}>{a.count}</span>
            </div>
          ))}
        </div>
      )}

      {/* Unsettled Invoices */}
      {unsettled.length > 0 && (
        <div style={{ ...cardStyle, padding: '16px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ ...sectionTitle, margin: 0 }}>🔴 Unsettled Invoices</h3>
            <button onClick={() => navigate('/company/invoicing/invoices?filter=unsettled')} style={linkBtn}>View All →</button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Invoice #</th>
                  <th style={thStyle}>Customer</th>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Due Date</th>
                  <th style={thStyle}>Total</th>
                  <th style={thStyle}>Paid</th>
                  <th style={thStyle}>Remaining</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {unsettled.map(inv => (
                  <tr key={inv.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/company/invoicing/invoices/${inv.id}`)}>
                    <td style={tdStyle}><span style={{ fontWeight: 600, color: '#3b82f6' }}>{inv.invoice_number}</span></td>
                    <td style={tdStyle}>{inv.client?.display_name || inv.customer_name}</td>
                    <td style={tdStyle}>{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : '—'}</td>
                    <td style={tdStyle}>
                      {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '—'}
                      {inv.due_date && new Date(inv.due_date) < new Date() && (
                        <span style={{ ...badgeStyle, background: '#fef2f2', color: '#dc2626', marginLeft: '6px' }}>OVERDUE</span>
                      )}
                    </td>
                    <td style={tdStyle}>{fmt(inv.total)}</td>
                    <td style={tdStyle}>{fmt(inv.paid_amount)}</td>
                    <td style={{ ...tdStyle, fontWeight: 600, color: '#ef4444' }}>{fmt(inv.remaining_amount)}</td>
                    <td style={tdStyle}>
                      <span style={{ ...badgeStyle, background: inv.payment_status === 'partially_paid' ? '#fef3c7' : '#fef2f2', color: inv.payment_status === 'partially_paid' ? '#d97706' : '#dc2626' }}>
                        {(inv.payment_status || '').replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Overdue Invoices */}
      {overdue.length > 0 && (
        <div style={{ ...cardStyle, padding: '16px', marginBottom: '20px' }}>
          <h3 style={sectionTitle}>🔥 Overdue Invoices</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Invoice #</th>
                  <th style={thStyle}>Customer</th>
                  <th style={thStyle}>Due Date</th>
                  <th style={thStyle}>Days Overdue</th>
                  <th style={thStyle}>Remaining</th>
                  <th style={thStyle}>Currency</th>
                </tr>
              </thead>
              <tbody>
                {overdue.map(inv => (
                  <tr key={inv.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/company/invoicing/invoices/${inv.id}`)}>
                    <td style={tdStyle}><span style={{ fontWeight: 600, color: '#3b82f6' }}>{inv.invoice_number}</span></td>
                    <td style={tdStyle}>{inv.client?.display_name || inv.customer_name}</td>
                    <td style={tdStyle}>{inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '—'}</td>
                    <td style={{ ...tdStyle, fontWeight: 700, color: '#dc2626' }}>{inv.days_overdue} days</td>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{fmt(inv.remaining_amount)}</td>
                    <td style={tdStyle}>{inv.currency}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Invoices + Recent Receipts side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
        <div style={{ ...cardStyle, padding: '16px' }}>
          <h3 style={sectionTitle}>📋 Recent Invoices</h3>
          {recentInv.length === 0 ? <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>No recent invoices</p> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Invoice #</th>
                    <th style={thStyle}>Customer</th>
                    <th style={thStyle}>Total</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentInv.map(inv => (
                    <tr key={inv.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/company/invoicing/invoices/${inv.id}`)}>
                      <td style={tdStyle}><span style={{ fontWeight: 600, color: '#3b82f6' }}>{inv.invoice_number}</span></td>
                      <td style={tdStyle}>{inv.client?.display_name || inv.customer_name}</td>
                      <td style={tdStyle}>{fmt(inv.total)}</td>
                      <td style={tdStyle}>
                        <span style={{ ...badgeStyle, ...paymentBadgeColor(inv.payment_status) }}>
                          {(inv.payment_status || '').replace(/_/g, ' ')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={{ ...cardStyle, padding: '16px' }}>
          <h3 style={sectionTitle}>🧾 Recent Receipts</h3>
          {recentRec.length === 0 ? <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>No recent receipts</p> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Receipt #</th>
                    <th style={thStyle}>Customer</th>
                    <th style={thStyle}>Invoice</th>
                    <th style={thStyle}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {recentRec.map(rec => (
                    <tr key={rec.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/company/invoicing/invoices/${rec.invoice_id}`)}>
                      <td style={tdStyle}><span style={{ fontWeight: 600, color: '#10b981' }}>{rec.receipt_number}</span></td>
                      <td style={tdStyle}>{rec.client?.display_name || rec.customer_name}</td>
                      <td style={tdStyle}>{rec.invoice?.invoice_number || '—'}</td>
                      <td style={tdStyle}>{fmt(rec.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function paymentBadgeColor(status) {
  switch (status) {
    case 'paid': return { background: '#dcfce7', color: '#16a34a' };
    case 'partially_paid': return { background: '#fef3c7', color: '#d97706' };
    case 'unpaid': return { background: '#fef2f2', color: '#dc2626' };
    default: return { background: '#f1f5f9', color: '#64748b' };
  }
}

const pageStyle = { padding: '24px', maxWidth: '1200px' };
const cardStyle = { background: '#fff', borderRadius: '10px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' };
const sectionTitle = { margin: '0 0 12px', fontSize: '1rem', fontWeight: 600 };
const selectStyle = { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', background: '#fff' };
const inputStyle = { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' };
const thStyle = { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' };
const tdStyle = { padding: '10px', borderBottom: '1px solid #f1f5f9', color: '#334155' };
const badgeStyle = { padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize' };
const linkBtn = { background: 'none', border: 'none', color: '#3b82f6', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' };
